import React from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useAppStore, useUserRoles } from '../stores/useAppStore'
import SyncIndicator from './SyncIndicator'
import { useMessageNotifications } from '../hooks/useMessageNotifications'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { messageService, type MessageCount } from '../services/messageService'
import {
  KeyIcon,
  TruckIcon,
  BellIcon,
  Cog6ToothIcon,
  UsersIcon,
  WrenchScrewdriverIcon,
  ArrowRightOnRectangleIcon
} from '@heroicons/react/24/outline'

const Navigation: React.FC = () => {
  const { user, isOnline, logout, addNotification, selectedDate, setSelectedDate } = useAppStore()
  const { isTechnician, isAdmin } = useUserRoles()
  const location = useLocation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  // Get message notification state
  const { notificationState, bgColorClass } = useMessageNotifications()

  // Fetch message count for badge
  const technicianId = user?.id ? Number(user.id) : null
  const { data: messageCount } = useQuery<MessageCount>({
    queryKey: ['message-count', technicianId, selectedDate],
    queryFn: () => technicianId ? messageService.getMessageCount(technicianId, selectedDate) : Promise.resolve({ unreadCount: 0, readButNotCompletedCount: 0, totalCount: 0 }),
    enabled: !!technicianId && isTechnician(),
    refetchInterval: 5000, // Refresh every 5 seconds to match notification hook
    staleTime: 1000, // Consider data stale after 1 second for immediate updates
  })

  const handleLogout = () => {
    logout()
    navigate('/login')
    addNotification({
      type: 'info',
      message: 'You have been logged out.',
      timestamp: Date.now()
    })
  }

  const handleLogoClick = async (e: React.MouseEvent) => {
    e.preventDefault()
    const isHomePage = location.pathname === '/' || location.pathname === '/work-orders'
    const today = new Date().toLocaleString('sv').split(' ')[0]

    if (isHomePage && selectedDate !== today) {
      // If on home page and date is not today, reset to today
      setSelectedDate(today)
      addNotification({
        type: 'info',
        message: 'Date reset to today',
        timestamp: Date.now()
      })
    } else if (isHomePage) {
      // If on home page with today's date, refresh data immediately
      addNotification({
        type: 'info',
        message: 'Refreshing data...',
        timestamp: Date.now()
      })
      await queryClient.refetchQueries({ queryKey: ['filtered-workorders-offline'] })
    } else {
      // If not on home page, navigate to home
      navigate('/')
    }
  }

  const handleNavClick = async (e: React.MouseEvent, path: string) => {
    e.preventDefault()

    // Check if we're already on this route
    const isCurrentRoute = path === '/'
      ? (location.pathname === '/' || location.pathname === '/work-orders')
      : location.pathname.startsWith(path)

    if (isCurrentRoute) {
      // Immediately refetch queries for current page
      addNotification({
        type: 'info',
        message: 'Refreshing data...',
        timestamp: Date.now()
      })

      switch (path) {
        case '/':
        case '/work-orders':
          await queryClient.refetchQueries({ queryKey: ['filtered-workorders-offline'] })
          break
        case '/runsheet':
          await Promise.all([
            queryClient.refetchQueries({ queryKey: ['runsheet'] }),
            queryClient.refetchQueries({ queryKey: ['technicians'] }),
            queryClient.refetchQueries({ queryKey: ['clientWorkOrders'] })
          ])
          break
        case '/messages':
          await Promise.all([
            queryClient.refetchQueries({ queryKey: ['technician-messages'] }),
            queryClient.refetchQueries({ queryKey: ['technician-message-count'] }),
            queryClient.refetchQueries({ queryKey: ['message-count'] })
          ])
          break
        case '/schedule':
          await queryClient.refetchQueries({ queryKey: ['schedule-workorders'] })
          break
        case '/jsa-travel':
          // Generic refetch for JSA Travel page
          await queryClient.refetchQueries()
          break
      }
    } else {
      // Navigate to different route
      navigate(path)
    }
  }

  const isActive = (path: string) => {
    if (path === '/') {
      return location.pathname === path
    }
    return location.pathname.startsWith(path)
  }

  const technicianNavItems = [
    // { path: '/work-orders', label: 'Work Orders', icon: ClipboardDocumentListIcon },
    { path: '/runsheet', label: 'Keylist', icon: KeyIcon },
    { path: '/jsa-travel', label: 'Travel JSA', icon: TruckIcon },
    { path: '/messages', label: 'Messages', icon: BellIcon },
  ]

  const adminNavItems = [
    { path: '/admin', label: 'Admin', icon: Cog6ToothIcon },
    { path: '/admin/users', label: 'Users', icon: UsersIcon },
    { path: '/admin/settings', label: 'Settings', icon: WrenchScrewdriverIcon },
  ]

  const navItems = isTechnician() ? technicianNavItems : isAdmin() ? adminNavItems : []

  return (
    <>
      {/* header bg color --color-primary-rgb: rgb(0, 67, 92); */}
      <header className="shadow-sm border-b sticky top-0 z-40 bg-[#00435c] text-white">
        <div className="container-mobile">
          <div className="flex items-center justify-between h-16">
            {/* Logo and title */}
            <div className="flex items-center space-x-3">
              <a href="/" onClick={handleLogoClick} className="flex items-center space-x-2 text-xl font-bold text-primary hover:text-primary/80 transition-smooth cursor-pointer">
                <img src="/coast-smoke-alarms-logo.png" alt="CSA" className="w-150 max-w-38" />
              </a>
            </div>

            {/* Navigation - visible on all screen sizes */}
            {user && (
              <nav className="flex items-center space-x-1">
                {navItems.map((item) => {
                  const Icon = item.icon
                  // Apply special styling for Messages link based on notification state
                  const isMessagesLink = item.path === '/messages'
                  const hasNotification = isMessagesLink && notificationState !== 'normal'

                  let linkClassName = `flex items-center space-x-2 px-3 py-2 rounded-lg text-sm font-medium transition-smooth`

                  // Notification colors take precedence over active state
                  if (hasNotification) {
                    // Apply notification background color for Messages link
                    linkClassName += ` ${bgColorClass} text-white`
                  } else if (isActive(item.path)) {
                    linkClassName += ' bg-primary text-primary-foreground'
                  } else {
                    linkClassName += ' text-white hover:text-white hover:bg-gray-100'
                  }

                  return (
                    <a
                      key={item.path}
                      href={item.path}
                      onClick={(e) => handleNavClick(e, item.path)}
                      className={linkClassName}
                    >
                      <div className="relative">
                        <Icon className="h-4 w-4" />
                        {/* Show badge for Messages link with incomplete count (not completed) */}
                        {isMessagesLink && messageCount && (messageCount.unreadCount + messageCount.readButNotCompletedCount) > 0 && (
                          <span className="absolute -top-2 -right-2 bg-blue-600 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center font-bold">
                            {messageCount.unreadCount + messageCount.readButNotCompletedCount}
                          </span>
                        )}
                      </div>
                      <span className="hidden md:inline">{item.label}</span>
                    </a>
                  )
                })}
              </nav>
            )}

            {/* Right side - user info and controls */}
            <div className="flex items-center space-x-3">
              {/* Sync indicator */}
              <SyncIndicator />
              
              {/* Connection status */}
              <div className={`
                flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-medium
                ${isOnline ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}
              `}>
                <div className={`w-2 h-2 rounded-full ${isOnline ? 'bg-green-500' : 'bg-red-500'}`}></div>
                {/* <span className="hidden sm:inline">{isOnline ? 'Online' : 'Offline'}</span> */}
              </div>

              {/* User info and logout */}
              {user && (
                <div className="flex items-center space-x-3">
                  <div className="text-right hidden md:block">
                    <div className="text-sm font-medium text-white">{user.name}</div>
                    {user.roles && user.roles.length > 0 && (
                      <div className="text-xs text-white">
                        {user.roles.join(', ')}
                      </div>
                    )}
                  </div>
                  <button
                    onClick={handleLogout}
                    className="flex items-center space-x-1 px-3 py-2 text-sm font-medium text-white hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-smooth"
                  >
                    <ArrowRightOnRectangleIcon className="h-4 w-4" />
                    <span className="hidden md:inline">Logout</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>
    </>
  )
}

export default Navigation 